import { useCallback, useMemo, useRef, useState } from 'react';
import clsx from 'clsx';
import { useParams, useSearchParams } from 'react-router-dom';
import { RaportHeader } from '@/components/Raport/RaportHeader';
import { usePagesCount, usePrintReport, usePublicReport } from '@/hooks';
import carImage from '@/assets/car.jpg';
import '@/styles/raport.css';
import {
  RaportShortcutSummary,
  RAPORT_SHORTCUT_TITLE,
  RAPORT_SHORTCUT_DESCRIPTION,
} from '@/components/Raport/RaportShortcutSummary/RaportShortcutSummary';
import { RaportMainParams } from '@/components/Raport/RaportMainParams/RaportMainParams';
import RaportCarBody from '@/components/Raport/RaportCarBody/RaportCarBody';
import RaportCarInner from '@/components/Raport/RaportCarInner/RaportCarInner';
import RaportTestDrive from '@/components/Raport/RaportTestDrive/RaportTestDrive';
import RaportEngine from '@/components/Raport/RaportEngine/RaportEngine';
import RaportCarBottom from '@/components/Raport/RaportCarBottom/RaportCarBottom';
import { LayoutSection } from '@/components/Layout/LayoutSection';
import { ReportGallerySections } from '@/components/ReportGallerySections';
import { RaportDiagnosticComputer } from '@/components/Raport/RaportDiagnosticComputer';
import { RaportRepairHistory } from '@/components/Raport/RaportRepairHistory';
import { RaportCepik } from '@/components/Raport/RaportCepik';
import { reportService } from '@/api/services/report.service';
import type { AvailableSection } from '@/api/mappers/public-report.mapper';
import { isVerifiedReportStatus } from '@/api/mappers/public-report.mapper';
import {
  generateReportMenuItems,
  generateAdditionalServicesItems,
} from '@/utils/reportMenuGenerator';
import { getBrandFavicon, getBrandLogo } from '@/helpers/brandLogo';
import { useFavicon } from '@/hooks/useFavicon';
import { APP_COLORS } from '@/config/colors';
import {
  prepareReportV2Data,
} from '@/api/mappers/hsp/prepareReportV2';
import type { MappedAddon } from '@/api/mappers/addons.mapper';
import { HSPLegal } from './componentsHSP/HSPLegal';
import { formatDate } from '@/helpers/helpers';
import { HSPEstimation } from './componentsHSP/HSPEstimation';
import { HSPMileageChart } from './componentsHSP/HSPMileageChart';
import { HSPVehicleIdentification } from './componentsHSP/HSPVehicleIdentification';
import { HSPTimeline } from './componentsHSP/HSPTimeline';
import { HSPHistory } from './componentsHSP/HSPHistory';
import { HSPSpecs } from './componentsHSP/HSPSpecs';
import { SectionSubtitle } from '@/components';
import { HSPEquipment } from './componentsHSP/HSPEquipment';
import { HSPFinancialSpecs } from './componentsHSP/HSPFinancialSpecs';
import { Layout } from './componentsHSP/Layout';
import {
  SidebarAccordion,
  SidebarSection,
} from './componentsHSP/SidebarAccordion/SidebarAccordion';
import { ReportAddons } from '@/components/ReportAddons';
import { RaportHeroSection } from '@/components/RaportHeroSection';
import { ReportLegalDisclaimer } from '@/components/ReportLegalDisclaimer';
import { ReportStatusView } from '@/components/ReportStatusView';
import { AuthorizedImageProvider } from '@/components/AuthorizedImage';
import {
  CustomerFeedbackBanner,
  CustomerFeedbackModalHost,
  CustomerFeedbackPdfDownloadGate,
  CustomerFeedbackProvider,
  CustomerFeedbackSticky,
} from '@/components/CustomerFeedback';
import type { SubmitFeedbackRequest } from '@/api/types/models.types';
import { usePublicGalleryImageResolver } from './use-public-gallery-image-resolver';

const HSP_VEHICLE_HISTORY_ADDON_TYPE = 'HSP_VEHICLE_HISTORY';

const isHspVehicleHistoryAddon = (addon: Pick<MappedAddon, 'type'>) =>
  addon.type === HSP_VEHICLE_HISTORY_ADDON_TYPE;

export interface RaportDetailsTempProps {
  /**
   * Id raportu przekazane jawnie, gdy komponent jest osadzony pod adresem
   * linku dostępowego (`/report-access/:linkId`) — w tym URL-u nie ma param `:id`.
   * Domyślnie brane z param `:id` w URL-u (`/raporty/:id`).
   */
  reportId?: string;
  /**
   * Ad-hoc token (quick-access). Domyślnie brany z `?token=` w URL-u.
   */
  token?: string;
}

export const RaportDetailsTemp = ({
  reportId,
  token: tokenProp,
}: RaportDetailsTempProps = {}) => {
  const { id: routeId } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const id = reportId ?? routeId;
  const token = tokenProp ?? searchParams.get('token') ?? undefined;

  const { reportData, loading, error } = usePublicReport({ id, token });
  const fallbackFavicon = getBrandFavicon();
  const brandFavicon = getBrandFavicon(reportData?.brandType);
  useFavicon(brandFavicon, fallbackFavicon);
  const resolvedGalleries = usePublicGalleryImageResolver(
    reportData?.galleries,
    token,
    id,
  );
  const { printReport, isPrinting } = usePrintReport();

  const reportContentRef = useRef<HTMLDivElement>(null);
  const calculatedPagesCount = usePagesCount(reportContentRef, [reportData], ['#gallery']);

  const menuItems = useMemo(() => generateReportMenuItems(reportData), [reportData]);
  const hspVehicleHistoryAddon = useMemo(
    () => reportData?.addons.find(isHspVehicleHistoryAddon) ?? null,
    [reportData?.addons],
  );
  const nonHspAddons = useMemo(
    () =>
      reportData?.addons.filter((addon) => !isHspVehicleHistoryAddon(addon)) ??
      [],
    [reportData?.addons],
  );
  const reportDataWithoutHspAddon = useMemo(
    () => (reportData ? { ...reportData, addons: nonHspAddons } : null),
    [reportData, nonHspAddons],
  );
  const hspHistoryNoDamage = useMemo(
    () =>
      reportData?.addons.some(
        (addon) =>
          isHspVehicleHistoryAddon(addon) && addon.status === 'NOT_FOUND',
      ) ?? false,
    [reportData?.addons],
  );

  const reportV2Data = useMemo(() => {
    if (!reportData?.hspSection) {
      return null;
    }

    const { header, sections, sidebarItems } = prepareReportV2Data(
      reportData.hspSection,
      undefined,
      { historyNoDamage: hspHistoryNoDamage }
    );
    return { header, sections, sidebarItems };
  }, [hspHistoryNoDamage, reportData?.hspSection]);

  const hspSidebarItems = useMemo<SidebarSection['items']>(() => {
    if (reportV2Data?.sidebarItems.length) {
      return reportV2Data.sidebarItems;
    }

    if (hspVehicleHistoryAddon && hspHistoryNoDamage) {
      return [
        {
          label: 'Historia szkód pojazdu',
          value: 'historia-szkod',
          elementId: 'historia-szkod',
          icon: 'car-accident',
        },
      ];
    }

    return [];
  }, [hspHistoryNoDamage, hspVehicleHistoryAddon, reportV2Data?.sidebarItems]);

  // Tworzenie sekcji dla SidebarAccordion
  const sidebarSections = useMemo<SidebarSection[]>(() => {
    const sections: SidebarSection[] = [];

    // Sekcja główna - Raport z oględzin
    if (menuItems.length > 0) {
      sections.push({
        id: 'report-main',
        title: 'Raport z oględzin',
        items: menuItems,
      });
    }

    // HSP renderuje się jako osobny blok raportu przed kartami dodatków,
    // więc sidebar musi mieć tę samą kolejność anchorów co DOM.
    if (hspSidebarItems.length > 0) {
      sections.push({
        id: 'report-hsp',
        title: '',
        items: hspSidebarItems,
      });
    }

    const additionalReportItems =
      generateAdditionalServicesItems(reportDataWithoutHspAddon);

    if (additionalReportItems.length > 0) {
      sections.push({
        id: 'report-additional',
        title: 'Raporty dodatkowe',
        items: additionalReportItems,
      });
    }

    return sections;
  }, [hspSidebarItems, menuItems, reportDataWithoutHspAddon]);

  // Lekka pozycja statusowa HSP nie ma własnych załączników; wynik HSP renderuje
  // się jako osobna sekcja raportu, a nie karta w "Dodatkowych usługach".
  const visibleAddons = useMemo(
    () => nonHspAddons,
    [nonHspAddons],
  );

  const [imagesDownloadError, setImagesDownloadError] = useState<string | null>(null);

  const handleDownloadImages = useCallback(async () => {
    try {
      await reportService.downloadGalleryZip(
        id ? `gallery-${id}.zip` : undefined,
        token,
      );
    } catch (err) {
      const message =
        (err as { message?: string })?.message || 'Nie udało się pobrać paczki zdjęć.';
      setImagesDownloadError(message);
    }
  }, [id, token]);

  const handleSubmitFeedback = useCallback(
    async (data: SubmitFeedbackRequest) => {
      const response = await reportService.submitFeedback(data);
      return response.data;
    },
    [],
  );

  if (loading) {
    return (
      <ReportStatusView
        variant="loading"
        title="Ładowanie danych raportu..."
        reportId={id}
      />
    );
  }

  const fetchError = error ?? imagesDownloadError;
  if (fetchError) {
    return (
      <ReportStatusView
        variant="error"
        title="Błąd podczas pobierania raportu"
        message={fetchError}
        reportId={id}
      />
    );
  }

  if (!reportData) {
    return <ReportStatusView variant="empty" title="Nie udało się pobrać danych raportu" />;
  }

  const carBasicInfo = reportData.carBasicInfo;
  const galleries = resolvedGalleries ?? reportData.galleries;
  const reportSummary = reportData.summary;
  const brandLogo = getBrandLogo(reportData.brandType);
  // Dane dla RaportMainParams
  const vehicleMainData = {
    brand: carBasicInfo.vehicleBrand,
    model: carBasicInfo.vehicleModel,
    productionYear: carBasicInfo.yearOfProduction || '',
    vin: carBasicInfo.vinNumber,
    mileage: carBasicInfo.mileage || '',
    countryOfOrigin: carBasicInfo.countryOfOrigin || '',
    fuelType: carBasicInfo.fuelType || '',
    engineDisplacement: carBasicInfo.engineDisplacement || '',
    enginePower: carBasicInfo.enginePower || '',
    driveType: carBasicInfo.driveType || '',
    engineLayout: carBasicInfo.engineLayout || '',
    transmission: carBasicInfo.transmission || '',
    bodyType: carBasicInfo.bodyType || '',
    doorCount: carBasicInfo.doorCount || '',
    color: carBasicInfo.color || '',
    maxWeight: carBasicInfo.maxWeight || '',
    registrationNumber: carBasicInfo.registrationNumber,
    insuranceExpiryDate: carBasicInfo.insuranceExpiryDate,
    firstRegistrationDate: carBasicInfo.firstRegistrationDate,
    nextTechnicalInspectionDate: carBasicInfo.nextTechnicalInspectionDate,
    firstRegistrationInPoland: carBasicInfo.firstRegistrationInPoland,
    isRegistrationNumberConsistent: carBasicInfo.isRegistrationNumberConsistent,
  };

  const has = (s: AvailableSection) => reportData.availableSections.has(s);
  const isVerified = isVerifiedReportStatus(reportData.reportStatus);

  const { isEmpty, isHspOnly } = reportData;
  const showHeroIntro = !isVerified && !isEmpty;
  const showIntroHero = isEmpty || showHeroIntro;
  const showReportHeader = isVerified && !isEmpty;
  const reportHeaderWrapperStyle = isHspOnly
    ? { backgroundColor: APP_COLORS.navy[950] }
    : undefined;

  // Karta "Nie wykazano historii szkodowej" jest zwykle renderowana w sekcji HSP
  // (na podstawie hspSection). Gdy hspSection jest niedostępne — np. widok
  // addons-only w statusie REPORT_EDITING — a dodatek HSP_VEHICLE_HISTORY zwrócił
  // NOT_FOUND, renderujemy tę kartę samodzielnie na podstawie statusu addona.
  const showStandaloneNoDamage = hspHistoryNoDamage && !reportV2Data;

  return (
    <AuthorizedImageProvider token={token}>
      <Layout
        title="Twój raport z ekspertyzy"
        hasSidebar={true}
        logo={<img src={brandLogo} alt="Logo" className="w-full" />}
        navbarContent={<>
          <div className="flex w-full max-w-[1348px] flex-col-reverse items-center gap-6 py-6 lg:flex-row lg:justify-end lg:gap-6 lg:px-8 print:hidden">
            <div className="flex flex-col gap-2 justify-center items-center lg:items-end lg:justify-end">
              <h1 className="text-[22px] font-bold text-text-primary lg:text-right lg:text-[26px]">
                Twój raport z ekspertyzy
              </h1>
            </div>
            <div className="hidden lg:block w-[1px] h-[50px] bg-gray-200"></div>
            <img src={brandLogo} alt="Logo" className="h-[52px] w-auto" />
          </div>
        </>}
        sidebarContent={<SidebarAccordion sections={sidebarSections} />}
        footerContent={<></>}
      >
        {/* TODO(feedback-assets): pass the final feedback banner asset when it is added to src/assets. */}
        <CustomerFeedbackProvider
          availability={reportData.feedbackContext}
          onSubmit={handleSubmitFeedback}
        >
          {isEmpty && (
            <RaportHeroSection
              title="Wstępne wyniki ekspertyzy twojego pojazdu są już dostępne"
              subtitle="Część raportów dodatkowych, niewymagających wizyty rzeczoznawcy na miejscu, jest dostępna poniżej"
              progressTitle="Pełny raport z oględzin pojazdu"
              progressLabel="W trakcie przygotowania"
            />
          )}
          {showHeroIntro && (
            <RaportHeroSection
              title="Wstępne wyniki ekspertyzy twojego pojazdu są już dostępne"
              subtitle="Część raportów dodatkowych, niewymagających wizyty rzeczoznawcy na miejscu, jest dostępna poniżej"
              progressTitle="Pełny raport z oględzin pojazdu"
              progressLabel="W trakcie przygotowania"
              progressPercentage={38}
            />
          )}
          <div ref={reportContentRef}>
            {showReportHeader && (
              <div
                className={clsx(
                  'p-4 lg:pl-[48px]',
                  isHspOnly ? 'lg:min-h-[340px]' : 'raport-bg raport-wrapper',
                )}
                style={reportHeaderWrapperStyle}
              >
                <CustomerFeedbackPdfDownloadGate onDownloadPdf={printReport}>
                  {(handleDownloadPdf) => (
                    <RaportHeader
                      vehicleBrand={carBasicInfo.vehicleBrand}
                      vehicleModel={carBasicInfo.vehicleModel}
                      vinNumber={carBasicInfo.vinNumber}
                      orderNumber={reportData?.externalOrderId || ''}
                      packageType={reportData?.reportType || ''}
                      reportDate={formatDate(reportData?.createdAt || '') || ''}
                      pagesCount={calculatedPagesCount}
                      vehicleType={carBasicInfo.vehicleType}
                      logoUrl={carBasicInfo.logoUrl || ''}
                      // W trybie HSP-only nie mamy realnego zdjęcia pojazdu — pomijamy
                      // `carImageUrl`, żeby `RaportHeader` nie wyrenderował białej karty galerii
                      // (która zasłaniałaby ciemne tło wrappera i wprowadzała w błąd "0 zdjęć").
                      carImageUrl={
                        isHspOnly
                          ? undefined
                          : galleries.mainPhotoUrl ||
                          carBasicInfo.carImageUrl ||
                          carImage
                      }
                      mainPhoto={isHspOnly ? undefined : galleries.mainPhoto}
                      onDownloadPdf={handleDownloadPdf}
                      pdfLoading={isPrinting}
                      photoCount={galleries.all.length}
                      onDownloadImages={handleDownloadImages}
                    />
                  )}
                </CustomerFeedbackPdfDownloadGate>
              </div>
            )}

            <div
              className={clsx(
                'ml-0',
                showReportHeader ? 'bg-transparent' : 'bg-white',
                isHspOnly && 'raport-sections--hsp-only',
              )}
            >
              {has('REPORT_SUMMARY') && reportSummary && (
                <div className="relative">
                  <div id="skrot-raportu" className="absolute -top-[500px]" />
                  <LayoutSection
                    title={showReportHeader ? '' : RAPORT_SHORTCUT_TITLE}
                    description={showReportHeader ? '' : RAPORT_SHORTCUT_DESCRIPTION}
                    icon="paper"
                    className={clsx(
                      showReportHeader && '!bg-transparent',
                      showIntroHero && '!mt-0',
                    )}
                  >
                    <RaportShortcutSummary
                      summaryCards={reportSummary.summaryCards}
                      overallRating={reportSummary.overallRating}
                      expertWouldBuy={reportSummary.expertWouldBuy}
                      inspectionSummary={reportSummary.inspectionSummary}
                      mileageVerificationDescription={
                        reportSummary.mileageVerificationDescription
                      }
                      mileageVerificationIsPositive={
                        reportSummary.mileageVerificationIsPositive
                      }
                      advertisementVerificationDescription={
                        reportSummary.advertisementVerificationDescription
                      }
                      advertisementVerificationIsPositive={
                        reportSummary.advertisementVerificationIsPositive
                      }
                      sellerInterviewDescription={
                        reportSummary.sellerInterviewDescription
                      }
                      availableSections={reportData.availableSections}
                      overlapHeader={showReportHeader}
                    />
                  </LayoutSection>
                </div>
              )}
              {/* CustomerFeedbackBanner zwraca null, gdy nie ma czego pokazać
                  (brak głosowania). Bez tego warunku LayoutSection renderowałby
                  pustą sekcję. */}
              {reportData.feedbackContext?.canVote === true &&
                reportData.feedbackContext?.alreadyVoted !== true && (
                  <LayoutSection className="py-0! mt-0">
                    <CustomerFeedbackBanner />
                  </LayoutSection>
                )}

              {has('CAR_BASIC_INFO') && (
                <LayoutSection
                  title="Podstawowe informacje"
                  description="Szczegółowe parametry techniczne dotyczące pojazdu."
                  icon="i"
                  id="podstawowe-informacje"
                >
                  <RaportMainParams
                    vehicleData={vehicleMainData}
                    mainPhotoUrl={
                      galleries.mainPhotoUrl || carBasicInfo.carImageUrl || carImage
                    }
                    mainPhoto={galleries.mainPhoto}
                  />
                </LayoutSection>
              )}

              {has('EXTERNAL_INSPECTION') && reportData.carBody && (
                <div id="car-body">
                  <RaportCarBody
                    ratingSection={reportData.carBody.ratingSection}
                    remarks={reportData.carBody.remarks}
                    paintAndGapMeasurements={reportData.carBody.paintAndGapMeasurements}
                    windowsData={reportData.carBody.windowsData}
                    tiresData={reportData.carBody.tiresData}
                    lightsData={reportData.carBody.lightsData}
                    galleryPhotos={galleries.body}
                    vehicleType={carBasicInfo.vehicleType}
                  />
                </div>
              )}

              {has('INTERNAL_INSPECTION') && reportData.carInterior && (
                <LayoutSection
                  title="Wnętrze"
                  description="Stan elementów kokpitu / Stan elementów wnętrza / Wyposażenie"
                  icon="trophy"
                  id="car-interior"
                >
                  <RaportCarInner
                    ratingSection={reportData.carInterior.ratingSection}
                    remarks={reportData.carInterior.remarks}
                    cockpitSection={reportData.carInterior.cockpitSection}
                    interiorSection={reportData.carInterior.interiorSection}
                    equipmentItems={reportData.carInterior.equipmentItems}
                    equipmentExpertSummary={reportData.carInterior.equipmentExpertSummary}
                    galleryPhotos={galleries.interior}
                  />
                </LayoutSection>
              )}

              {has('ENGINE_AND_TRUNK_COMPARTMENT') && reportData.engineAndTrunk && (
                <LayoutSection
                  title="Komora silnika i bagażnika"
                  description="Stan komory silnika / Stan komory bagażnika"
                  icon="handshake"
                  id="engine-trunk"
                >
                  <RaportEngine
                    ratingSection={reportData.engineAndTrunk.ratingSection}
                    remarks={reportData.engineAndTrunk.remarks}
                    engineCompartmentItems={reportData.engineAndTrunk.engineCompartmentItems}
                    trunkCompartmentItems={reportData.engineAndTrunk.trunkCompartmentItems}
                    engineExpertSummary={reportData.engineAndTrunk.engineExpertSummary}
                    trunkExpertSummary={reportData.engineAndTrunk.trunkExpertSummary}
                    galleryPhotos={galleries.engineCompartment}
                  />
                </LayoutSection>
              )}

              {has('TEST_DRIVE') && reportData.testDrive && (
                <LayoutSection
                  title="Jazda próbna"
                  description="Wnioski sporządzone podczas jazdy próbnej."
                  icon="steering"
                  id="test-drive"
                >
                  <RaportTestDrive
                    ratingSection={reportData.testDrive.ratingSection}
                    remarks={reportData.testDrive.remarks}
                    testDriveItems={reportData.testDrive.testDriveItems}
                    expertSummary={reportData.testDrive.expertSummary}
                    drivenBy={reportData.testDrive.drivenBy}
                  />
                </LayoutSection>
              )}

              {has('CAR_CHASSIS') && reportData.chassis && (
                <LayoutSection
                  title="Podwozie"
                  description="Błędy i nieprawidłowości wykryte podczas badania na kanale w warsztacie diagnostycznym."
                  icon="network-nodes"
                  id="chassis"
                >
                  <RaportCarBottom
                    ratingSection={reportData.chassis.ratingSection}
                    remarks={reportData.chassis.remarks}
                    chassisItems={reportData.chassis.chassisItems}
                    expertSummary={reportData.chassis.expertSummary}
                    galleryPhotos={galleries.undercarriage}
                  />
                </LayoutSection>
              )}

              {has('COMPUTER_DIAGNOSTICS') && reportData.computerDiagnostics && (
                <LayoutSection
                  title="Komputer diagnostyczny"
                  description="Błędy i nieprawidłowości wykryte podczas badania komputerem diagnostycznym."
                  icon="monitor-screen"
                  id="computer-diagnostics"
                >
                  <RaportDiagnosticComputer
                    hasErrors={reportData.computerDiagnostics.hasErrors}
                    readStatus={reportData.computerDiagnostics.readStatus}
                    errors={reportData.computerDiagnostics.errors}
                    expertSummary={reportData.computerDiagnostics.expertSummary}
                    isCommunicationSuccessful={reportData.computerDiagnostics.isCommunicationSuccessful}
                    galleryPhotos={galleries.diagnostics}
                  />
                </LayoutSection>
              )}

              {has('REPAIR_HISTORY') && reportData.repairHistory && (
                <LayoutSection
                  title="Historia napraw"
                  description="Chronologiczna historia napraw pojazdu."
                  icon="wrench-tool"
                  id="historia-napraw"
                >
                  <RaportRepairHistory
                    historyItems={reportData.repairHistory.historyItems}
                    expertSummary={reportData.repairHistory.expertSummary}
                    galleryPhotos={galleries.documentation}
                  />
                </LayoutSection>
              )}

              {/* Sekcja CEPiK */}
              {reportData.cepikSection && (
                <LayoutSection
                  title="Informacje o pojeździe"
                  description="Dane o pojeździe dostępne w publicznych bazach danych"
                  icon="copyright"
                  id="informacje-o-pojezdzie"
                >
                  <RaportCepik
                    technicalData={reportData.cepikSection.technicalData}
                    documents={reportData.cepikSection.documents}
                    homologation={reportData.cepikSection.homologation}
                    eventsSummary={reportData.cepikSection.eventsSummary}
                    currentStatus={reportData.cepikSection.currentStatus}
                    timelineEvents={reportData.cepikSection.timelineEvents}
                  />
                </LayoutSection>
              )}

              {/* {reportV2Data?.sections?.summary.visible && reportV2Data.sections.summary.stats && (
            <LayoutSection
              title=""
              description={
                "Ogólne informacje o raporcie i pojezdzie"
              }
              icon="apron-clothing"
              id="hsp-podsumowanie"
            >
              <HSPSummary
                raport={reportData.hspSection as ApiResponseRaportV2}
                data={reportV2Data.sections.summary.stats}
                evaluation={reportV2Data.sections.summary.evaluation}
                sections={reportV2Data.sections}
              />
            </LayoutSection>
          )} */}

              {/* {reportV2Data?.sections?.warnings?.visible && (
            <LayoutSection
              title={"Rekomendacje i ostrzeżenia"}
              description={
                "Zestawienie wykrytych problemów i weryfikacja w oparciu o stopień ryzyka zakupu."
              }
              icon="warning"
              id="rekomendacje-ostrzezenia"
            >
              <HSPRecommendationsWarnings
                data={{
                  warnings: reportV2Data?.sections?.warnings?.items,
                }}
              />
            </LayoutSection>
          )} */}

              {/* Galeria - wszystkie zdjęcia z podziałem na kategorie */}
              {galleries.all.length > 0 && (
                <ReportGallerySections galleries={galleries} />
              )}

              {reportV2Data?.sections?.identification?.visible && (
                <LayoutSection
                  title={"Dane identyfikacyjne"}
                  description={"Skrót informacji identyfikacyjnych pojazdu."
                  }
                  icon="i"
                  id="dane-identyfikacyjne"
                >
                  <HSPVehicleIdentification
                    data={reportV2Data?.sections?.identification?.data}
                    onShowFullVehicleCard={() =>
                      (window.location.href = "#dane-techniczne")
                    }
                  />
                </LayoutSection>
              )}

              {reportV2Data?.sections?.timeline?.visible && (
                <LayoutSection
                  title={"Oś czasu pojazdu"}
                  description={
                    "Historyczne dane dotyczące pojazdu w formie osi czasu."
                  }
                  icon="fish-swimming"
                  id="os-czasu"
                >
                  <HSPTimeline historyData={reportV2Data?.sections?.timeline?.data} />
                </LayoutSection>
              )}
              {reportV2Data?.sections?.mileage?.visible && (
                <LayoutSection
                  title={"Wykres przebiegów"}
                  description={
                    "Graficzna prezentacja wszystkich odnotowanych przebiegów pojazdu w formie wykresu."
                  }
                  icon="speedometer"
                  id="historia-przebiegu"
                >
                  <HSPMileageChart entries={reportV2Data?.sections?.mileage?.entries} />
                </LayoutSection>
              )}

              {reportV2Data?.sections?.history?.visible && (
                <LayoutSection
                  title={"Historia szkód pojazdu"}
                  description={
                    "Szczegółowe parametry historii napraw i uszkodzeń."
                  }
                  icon="car-accident"
                  id="historia-szkod"
                >
                  <HSPHistory
                    historyData={reportV2Data?.sections?.history?.entries}
                    reportStats={reportV2Data?.sections?.summary?.stats}
                    noDamage={reportV2Data?.sections?.history?.noDamage}
                  />
                </LayoutSection>
              )}

              {/* Fallback "brak szkód" bez hspSection — sterowany statusem addona
                  HSP_VEHICLE_HISTORY=NOT_FOUND (m.in. widok addons-only).
                  Zerujemy tylko górny margines sekcji (nadmiarowy odstęp od hero),
                  ale wewnętrzne odstępy karty zostawiamy — HSPHistory renderuje
                  NoDamageHistoryCard z własnym paddingiem. */}
              {showStandaloneNoDamage && (
                <LayoutSection
                  title="Historia szkód pojazdu"
                  description="Szczegółowe parametry historii napraw i uszkodzeń."
                  icon="car-accident"
                  id="historia-szkod"
                  className="!mt-0 lg:!mt-0"
                >
                  <HSPHistory historyData={[]} noDamage />
                </LayoutSection>
              )}

              {reportV2Data?.sections?.legal?.visible && (
                <LayoutSection
                  title={"Status prawny"}
                  description={
                    "Sprawdzenie w bazach danych krajowych i międzynarodowych"
                  }
                  icon="libra"
                  id="status-prawny"
                >
                  <HSPLegal
                    pledgeRegisters={reportV2Data?.sections?.legal?.theftInfo?.pledgeRegisters}
                    theftDatabases={reportV2Data?.sections?.legal?.theftInfo?.stolenDatabases}
                    verificationDate={reportV2Data?.sections?.legal?.theftInfo?.checkedAt}
                  />
                </LayoutSection>
              )}

              {reportV2Data?.sections?.estimation?.visible && reportV2Data?.sections?.estimation?.evaluation && (
                <LayoutSection
                  title={"Wycena wartości pojazdu"}
                  description={
                    "Szczegóły dotyczące wartości pojazdu oraz wszystkich elementów jego wyposażenia."
                  }
                  icon="ruler-measure"
                  id="wycena"
                >
                  <HSPEstimation evaluation={reportV2Data?.sections?.estimation?.evaluation} />
                </LayoutSection>
              )}

              {reportV2Data?.sections?.equipment.visible && (
                <LayoutSection
                  title={"Wyposażenie pojazdu"}
                  description={"Pełne zestawienie wyposażenia pojazdu."}
                  icon="trophy"
                  id="wyposazenie-pojazdu"
                >
                  {reportV2Data?.sections?.equipment.hasStandardDetails && (
                    <div className="mt-20">
                      {reportV2Data?.sections?.equipment.hasFinancialSpecs && (
                        <div className="mt-10">
                          <HSPFinancialSpecs
                            evaluation={reportV2Data.sections.equipment.evaluation}
                            equipment={reportV2Data.sections.equipment.equipment}
                          />
                        </div>
                      )}
                      {reportV2Data?.sections?.equipment.standardTabs.length > 0 && (
                        <div className="mt-20" id="wyposazenie-standardowe">
                          <SectionSubtitle
                            title={
                              "Wyposażenie standardowe"
                            }
                            icon="text-abc"
                          />
                          <HSPEquipment
                            data={reportV2Data?.sections?.equipment.standardTabs}
                            title={
                              "Wyposażenie standardowe"
                            }
                          />
                        </div>
                      )}
                    </div>
                  )}
                  {reportV2Data?.sections?.equipment.optionalTabs.length > 0 && (
                    <div id="wyposazenie-opcjonalne" className="mt-20">
                      <SectionSubtitle
                        title={"Wyposażenie opcjonalne"}
                        icon="star-rating"
                      />
                      <HSPEquipment
                        data={reportV2Data?.sections?.equipment.optionalTabs}
                        title={"Wyposażenie opcjonalne"}
                      />
                    </div>
                  )}
                </LayoutSection>
              )}



              {reportV2Data?.sections?.specs.visible && reportV2Data?.sections?.specs.data && (
                <LayoutSection
                  title={"Dane techniczne"}
                  description={
                    "Parametry techniczne i specyfikacja pojazdu"
                  }
                  icon="car"
                  id="dane-techniczne"
                >
                  <HSPSpecs
                    data={reportV2Data?.sections?.specs.data}
                  />
                </LayoutSection>
              )}


              {/* Sekcja Film wideo z oględzin */}
              {/* {reportData.videoUrl && (
            <LayoutSection
              title="Film wideo z oględzin"
              description="Zobacz i usłysz swoje przyszłe auto bezpośrednio na filmie."
              icon="video-camera"
              id="video"
            >
              <VideoPlayer
                videoUrl={reportData.videoUrl}
                thumbnailUrl={reportData.videoThumbnailUrl}
                vehicleDescription={`${carBasicInfo.vehicleBrand} ${carBasicInfo.vehicleModel}`}
              />
            </LayoutSection>
          )} */}

              {/* Sekcja Raporty dodatkowe (addony) */}
              {visibleAddons.length > 0 && (
                <LayoutSection
                  title="Dodatkowe usługi"
                  description="Załączniki i wyniki dodatkowych usług dołączone do raportu."
                  icon="paper"
                  id="dodatkowe-uslugi"
                >
                  <ReportAddons
                    addons={visibleAddons}
                    vehicleDescription={`${carBasicInfo.vehicleBrand} ${carBasicInfo.vehicleModel}`}
                  />
                </LayoutSection>
              )}

              <LayoutSection className='!mt-0 !lg:mt-[60px]'>
                <ReportLegalDisclaimer />
              </LayoutSection>
            </div>
          </div>
          <CustomerFeedbackSticky />
          <CustomerFeedbackModalHost />
        </CustomerFeedbackProvider>
      </Layout >
    </AuthorizedImageProvider>
  );
};

export default RaportDetailsTemp;
